import { useState, useEffect } from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    Chip,
    CircularProgress,
    Divider,
    Grid,
    LinearProgress,
    Paper,
    Stack,
    TextField,
    Typography,
    useTheme,
} from "@mui/material";
import {
    Warning as WarningIcon,
    CalendarToday as CalendarIcon,
    TrendingUp as TrendingUpIcon,
    Error as ErrorIcon,
    Groups as GroupsIcon,
    PlayArrow as PlayIcon,
    LocalShipping as SupplyIcon,
    Gavel as RegulatoryIcon,
    Engineering as ResourceIcon,
    Inventory as MaterialIcon,
    Lightbulb as RecommendIcon,
} from "@mui/icons-material";
import { ScenarioPhase, ScenarioImpact } from "../types";
import { useScenarioPhases, useAffectedProjects, useCalculateScenarioImpact } from "../hooks";

const SEVERITY_COLORS = {
    critical: "#d32f2f",
    high: "#ed6c02",
    medium: "#eab308",
    low: "#2e7d32",
};

export const ScenarioModeller = () => {
    const theme = useTheme();
    const [delayDuration, setDelayDuration] = useState(21);
    const [selectedPhases, setSelectedPhases] = useState<ScenarioPhase[]>([]);
    const [hasSimulated, setHasSimulated] = useState(false);

    const { data: phases, isLoading: isPhasesLoading } = useScenarioPhases();
    const selectedPhaseIds = selectedPhases.filter(p => p.isSelected).map(p => p.id);
    const { data: affectedProjects = [], isLoading: isProjectsLoading } = useAffectedProjects(selectedPhaseIds);
    const { mutate: calculateImpact, data: impact, isPending: isCalculating } = useCalculateScenarioImpact();

    useEffect(() => {
        if (phases && selectedPhases.length === 0) {
            setSelectedPhases(phases);
        }
    }, [phases]);

    const handlePhaseToggle = (phaseId: string) => {
        setSelectedPhases((prev) =>
            prev.map((phase) =>
                phase.id === phaseId ? { ...phase, isSelected: !phase.isSelected } : phase
            )
        );
        setHasSimulated(false);
    };

    const handleDelayChange = (value: number) => {
        setDelayDuration(value);
        setHasSimulated(false);
    };

    const handleSimulate = () => {
        if (selectedPhaseIds.length > 0) {
            setHasSimulated(true);
            calculateImpact({ delayDuration, selectedPhaseIds });
        }
    };

    const selectedPhasesCount = selectedPhases.filter((p) => p.isSelected).length;
    const weeksDelay = Math.floor(delayDuration / 7);

    if (isPhasesLoading) {
        return (
            <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ height: "100%", overflow: "auto", px: 2, py: 3 }}>
            <Grid container spacing={3}>
                {/* Left Panel - Scenario Parameters */}
                <Grid size={{ xs: 12, md: 5 }} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <Card variant="outlined" sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
                        <Box sx={{ display: "flex", alignItems: "start", flexDirection: "column" }}>
                            <Typography variant="body2" fontWeight={600}>
                                Scenario Parameters
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Configure delay scenario then run simulation
                            </Typography>
                        </Box>

                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                            <Typography variant="body2">Delay Duration (days)</Typography>
                            <TextField
                                type="number"
                                value={delayDuration}
                                onChange={(e) => handleDelayChange(Number(e.target.value))}
                                size="small"
                                fullWidth
                            />
                            <Typography variant="caption" color="text.secondary">
                                Apply a delay of {delayDuration} days ({weeksDelay} weeks)
                            </Typography>
                        </Box>

                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                            <Typography variant="body2">Affected Phases</Typography>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                {selectedPhases.map((phase) => (
                                    <Chip
                                        key={phase.id}
                                        label={`${phase.name}${phase.code ? ` (${phase.code})` : ""}`}
                                        variant={phase.isSelected ? "filled" : "outlined"}
                                        color={phase.isSelected ? "primary" : "default"}
                                        onClick={() => handlePhaseToggle(phase.id)}
                                        sx={{ fontSize: "0.65rem", borderRadius: 1, p: .25 }}
                                    />
                                ))}
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                                {selectedPhasesCount} phase{selectedPhasesCount !== 1 ? 's' : ''} selected
                            </Typography>
                        </Box>

                        {/* Simulate Button */}
                        <Button
                            variant="contained"
                            size="large"
                            startIcon={<PlayIcon />}
                            onClick={handleSimulate}
                            disabled={selectedPhasesCount === 0 || isCalculating}
                            sx={{
                                mt: 1,
                                py: 1.5,
                                fontWeight: 700,
                                fontSize: "0.9rem",
                                background: "linear-gradient(135deg, #1976d2, #1565c0)",
                                "&:hover": { background: "linear-gradient(135deg, #1565c0, #0d47a1)" },
                            }}
                        >
                            {isCalculating ? "Simulating..." : "Simulate Impact"}
                        </Button>
                    </Card>

                    <Card variant="outlined" sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
                        <Box sx={{ display: "flex", alignItems: "start", flexDirection: "column" }}>
                            <Typography variant="body2" fontWeight={600}>Affected Projects</Typography>
                            <Typography variant="caption" color="text.secondary">
                                {affectedProjects.length} projects in selected phases
                            </Typography>
                        </Box>
                        <Box sx={{ maxHeight: 250, overflowY: "auto", border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                            {isProjectsLoading ? (
                                <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}><CircularProgress size={24} /></Box>
                            ) : affectedProjects.map((project) => (
                                <Box key={project.id} sx={{ p: 1.5, borderBottom: "1px solid", borderColor: "divider", "&:last-child": { borderBottom: "none" }, "&:hover": { bgcolor: "action.hover" }, display: "flex", flexDirection: "column" }}>
                                    <Typography variant="caption">{project.name}</Typography>
                                    <Typography variant="caption" color="text.secondary">{project.code} • {project.phase}</Typography>
                                </Box>
                            ))}
                        </Box>
                    </Card>
                </Grid>

                {/* Right Panel - Simulation Results */}
                <Grid size={{ xs: 12, md: 7 }} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    {!hasSimulated && !isCalculating && (
                        <Box sx={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: 8, opacity: 0.6 }}>
                            <PlayIcon sx={{ fontSize: 64, color: "text.disabled", mb: 2 }} />
                            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>Configure & Simulate</Typography>
                            <Typography variant="body2" color="text.disabled" sx={{ maxWidth: 320, textAlign: "center" }}>
                                Set your delay duration, select affected phases, then click Simulate Impact to see the full analysis.
                            </Typography>
                        </Box>
                    )}

                    {isCalculating && (
                        <Card variant="outlined" sx={{ p: 4, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2, minHeight: 300 }}>
                            <CircularProgress size={48} />
                            <Typography variant="body1" fontWeight={600}>Running Simulation...</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", maxWidth: 360 }}>
                                Analysing {delayDuration}-day delay across {selectedPhasesCount} phases. Calculating resource conflicts, supply chain impact, and regulatory exposure.
                            </Typography>
                            <Box sx={{ width: "80%", mt: 2 }}>
                                <LinearProgress />
                            </Box>
                        </Card>
                    )}

                    {hasSimulated && !isCalculating && impact && (
                        <>
                            {/* Overall Risk Score */}
                            <Card variant="outlined" sx={{ p: 2, display: "flex", alignItems: "center", gap: 3, bgcolor: impact.overallRiskScore > 70 ? "rgba(211, 47, 47, 0.06)" : impact.overallRiskScore > 40 ? "rgba(237, 108, 2, 0.06)" : "rgba(46, 125, 50, 0.06)" }}>
                                <Box sx={{ position: "relative", display: "inline-flex" }}>
                                    <CircularProgress
                                        variant="determinate"
                                        value={impact.overallRiskScore}
                                        size={64}
                                        thickness={5}
                                        sx={{ color: impact.overallRiskScore > 70 ? "error.main" : impact.overallRiskScore > 40 ? "warning.main" : "success.main" }}
                                    />
                                    <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                        <Typography variant="body2" fontWeight={900} color={impact.overallRiskScore > 70 ? "error.main" : impact.overallRiskScore > 40 ? "warning.main" : "success.main"}>
                                            {impact.overallRiskScore}
                                        </Typography>
                                    </Box>
                                </Box>
                                <Box>
                                    <Typography variant="body2" fontWeight={700}>Overall Risk Score</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {impact.overallRiskScore > 70 ? "Critical — immediate action required" : impact.overallRiskScore > 40 ? "Elevated — mitigation recommended" : "Manageable — monitor closely"}
                                    </Typography>
                                </Box>
                                <Chip
                                    label={impact.overallRiskScore > 70 ? "CRITICAL" : impact.overallRiskScore > 40 ? "ELEVATED" : "LOW"}
                                    size="small"
                                    sx={{ ml: "auto", fontWeight: 700, bgcolor: impact.overallRiskScore > 70 ? "error.main" : impact.overallRiskScore > 40 ? "warning.main" : "success.main", color: "white" }}
                                />
                            </Card>

                            {/* Key Metrics Grid */}
                            <Grid container spacing={1.5}>
                                <Grid size={4}>
                                    <Paper variant="outlined" sx={{ p: 1.5, bgcolor: "rgba(211, 47, 47, 0.06)", borderColor: "error.main" }}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                                            <ErrorIcon sx={{ fontSize: 14 }} color="error" />
                                            <Typography variant="caption" color="error.main" fontWeight={600}>Integrity Risks</Typography>
                                        </Box>
                                        <Typography variant="h5" color="error.main" fontWeight={800}>{impact.newIntegrityRisks}</Typography>
                                        <Typography variant="caption" color="text.secondary">critical path breaches</Typography>
                                    </Paper>
                                </Grid>
                                <Grid size={4}>
                                    <Paper variant="outlined" sx={{ p: 1.5, bgcolor: "rgba(237, 108, 2, 0.06)", borderColor: "warning.main" }}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                                            <TrendingUpIcon sx={{ fontSize: 14 }} color="warning" />
                                            <Typography variant="caption" color="warning.main" fontWeight={600}>Budget at Risk</Typography>
                                        </Box>
                                        <Typography variant="h5" color="warning.dark" fontWeight={800}>£{(impact.budgetAtRisk / 1000).toFixed(0)}k</Typography>
                                        <Typography variant="caption" color="text.secondary">potential overrun</Typography>
                                    </Paper>
                                </Grid>
                                <Grid size={4}>
                                    <Paper variant="outlined" sx={{ p: 1.5, bgcolor: "rgba(25, 118, 210, 0.06)", borderColor: "info.main" }}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                                            <CalendarIcon sx={{ fontSize: 14 }} color="info" />
                                            <Typography variant="caption" color="info.main" fontWeight={600}>Schedule Overrun</Typography>
                                        </Box>
                                        <Typography variant="h5" color="info.dark" fontWeight={800}>{impact.scheduleOverrun}d</Typography>
                                        <Typography variant="caption" color="text.secondary">total programme slip</Typography>
                                    </Paper>
                                </Grid>
                                <Grid size={4}>
                                    <Paper variant="outlined" sx={{ p: 1.5 }}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                                            <ResourceIcon sx={{ fontSize: 14 }} color="action" />
                                            <Typography variant="caption" fontWeight={600}>Resource Conflicts</Typography>
                                        </Box>
                                        <Typography variant="h5" fontWeight={800}>{impact.resourceConflicts}</Typography>
                                        <Typography variant="caption" color="text.secondary">crew reallocation needed</Typography>
                                    </Paper>
                                </Grid>
                                <Grid size={4}>
                                    <Paper variant="outlined" sx={{ p: 1.5 }}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                                            <MaterialIcon sx={{ fontSize: 14 }} color="action" />
                                            <Typography variant="caption" fontWeight={600}>Material Delays</Typography>
                                        </Box>
                                        <Typography variant="h5" fontWeight={800}>{impact.materialDelays}</Typography>
                                        <Typography variant="caption" color="text.secondary">orders to reschedule</Typography>
                                    </Paper>
                                </Grid>
                                <Grid size={4}>
                                    <Paper variant="outlined" sx={{ p: 1.5 }}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                                            <RegulatoryIcon sx={{ fontSize: 14 }} color="action" />
                                            <Typography variant="caption" fontWeight={600}>Regulatory Exposure</Typography>
                                        </Box>
                                        <Typography variant="h5" fontWeight={800}>{impact.regulatoryExposure}</Typography>
                                        <Typography variant="caption" color="text.secondary">permits at risk</Typography>
                                    </Paper>
                                </Grid>
                            </Grid>

                            {/* Cascade Effects */}
                            <Card variant="outlined" sx={{ p: 2 }}>
                                <Typography variant="body2" fontWeight={700} sx={{ mb: 1.5 }}>Cascade Impact Analysis</Typography>
                                <Stack spacing={1}>
                                    {impact.cascadeEffects.map((effect, i) => (
                                        <Box key={i} sx={{ display: "flex", alignItems: "start", gap: 1.5, p: 1.5, borderRadius: 1, bgcolor: "action.hover" }}>
                                            <Chip
                                                label={effect.severity.toUpperCase()}
                                                size="small"
                                                sx={{ fontSize: "0.6rem", fontWeight: 700, minWidth: 60, bgcolor: SEVERITY_COLORS[effect.severity], color: "white" }}
                                            />
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="caption" fontWeight={700}>{effect.area}</Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>{effect.description}</Typography>
                                            </Box>
                                            <Chip label={`${effect.affectedProjects} projects`} size="small" variant="outlined" sx={{ fontSize: "0.6rem" }} />
                                        </Box>
                                    ))}
                                </Stack>
                            </Card>

                            {/* Recommendations */}
                            <Card variant="outlined" sx={{ p: 2, bgcolor: "rgba(25, 118, 210, 0.04)" }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                                    <RecommendIcon color="primary" fontSize="small" />
                                    <Typography variant="body2" fontWeight={700}>AI Recommendations</Typography>
                                </Box>
                                <Stack spacing={1}>
                                    {impact.recommendations.map((rec, i) => (
                                        <Box key={i} sx={{ display: "flex", alignItems: "start", gap: 1 }}>
                                            <Typography variant="caption" color="primary.main" fontWeight={700} sx={{ mt: 0.2 }}>{i + 1}.</Typography>
                                            <Typography variant="caption">{rec}</Typography>
                                        </Box>
                                    ))}
                                </Stack>
                            </Card>
                        </>
                    )}
                </Grid>
            </Grid>
        </Box>
    );
};
